/**
 * Replay Lab (Phase 1b) hooks — React Query wrappers over the admin API
 * (backend/app/routers/replay_lab.py). snake_case → camelCase for top-level
 * fields happens here (house convention, mirrors useInbox.ts transforms);
 * nested payloads pass through verbatim — see types/replayLab.ts.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/services/apiClient'
import type {
  ReplayRunDetail,
  ReplayScenarioSummary,
} from '@/types/replayLab'

/** Poll a run while it is executing; stop the moment it reaches a terminal
 * status. Returned to React Query's `refetchInterval`. */
export function replayRunRefetchInterval(status: string | undefined): number | false {
  return status === 'running' ? 1500 : false
}

/** Poll the scenario list while any row's most-recent run is still running,
 * so its status badge live-updates, then quiesce. */
export function replayScenariosRefetchInterval(
  rows: { lastRunStatus: string | null }[] | undefined,
): number | false {
  return rows?.some((r) => r.lastRunStatus === 'running') ? 2000 : false
}

function transformScenario(s: Record<string, unknown>): ReplayScenarioSummary {
  return {
    id: s.id as string,
    name: s.name as string,
    sourceConversationId: (s.source_conversation_id as string) ?? null,
    personaId: (s.persona_id as string) ?? null,
    channel: (s.channel as string) ?? 'whatsapp',
    language: (s.language as string) ?? 'es',
    anonymized: (s.anonymized as boolean) ?? true,
    createdBy: (s.created_by as string) ?? null,
    createdAt: s.created_at as string,
    lastRunId: (s.last_run_id as string) ?? null,
    lastRunStatus: (s.last_run_status as string) ?? null,
    lastRunAt: (s.last_run_at as string) ?? null,
  }
}

function transformRun(r: Record<string, unknown>): ReplayRunDetail {
  return {
    id: r.id as string,
    scenarioId: r.scenario_id as string,
    mode: (r.mode as string) ?? 'verbatim',
    configFingerprint: (r.config_fingerprint as Record<string, unknown>) ?? {},
    transcript: (r.transcript as ReplayRunDetail['transcript']) ?? [],
    agendaTrace: (r.agenda_trace as ReplayRunDetail['agendaTrace']) ?? [],
    checks: (r.checks as ReplayRunDetail['checks']) ?? {},
    status: r.status as string,
    error: (r.error as string) ?? null,
    createdBy: (r.created_by as string) ?? null,
    createdAt: r.created_at as string,
  }
}

export function useReplayScenarios(enabled = true) {
  return useQuery<ReplayScenarioSummary[], Error>({
    queryKey: ['replay', 'scenarios'],
    queryFn: async () => {
      const rows = await apiClient.get<Record<string, unknown>[]>('/api/replay/scenarios')
      return rows.map(transformScenario)
    },
    enabled,
    refetchInterval: (query) => replayScenariosRefetchInterval(query.state.data),
  })
}

export function useReplayRun(runId: string | undefined) {
  return useQuery<ReplayRunDetail, Error>({
    queryKey: ['replay', 'runs', runId ?? ''],
    queryFn: async () =>
      transformRun(await apiClient.get<Record<string, unknown>>(`/api/replay/runs/${runId}`)),
    enabled: !!runId,
    refetchInterval: (query) => replayRunRefetchInterval(query.state.data?.status),
  })
}

export function useRunScenario() {
  const qc = useQueryClient()
  return useMutation<ReplayRunDetail, Error, string>({
    mutationFn: async (scenarioId) =>
      transformRun(
        await apiClient.post<Record<string, unknown>>(
          `/api/replay/scenarios/${scenarioId}/run`,
          { mode: 'verbatim' },
        ),
      ),
    onSuccess: (run) => {
      // Prime the detail cache so the post-run navigation renders instantly.
      qc.setQueryData(['replay', 'runs', run.id], run)
      // The list badge updates via its own poll while the run is running.
      qc.invalidateQueries({ queryKey: ['replay', 'scenarios'] })
    },
  })
}

export function useDeleteScenario() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: async (scenarioId) => {
      await apiClient.delete(`/api/replay/scenarios/${scenarioId}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['replay', 'scenarios'] })
    },
  })
}

export function useSaveScenario() {
  const qc = useQueryClient()
  return useMutation<
    ReplayScenarioSummary,
    Error,
    { conversationId: string; name: string; anonymize: boolean }
  >({
    mutationFn: async ({ conversationId, name, anonymize }) =>
      transformScenario(
        await apiClient.post<Record<string, unknown>>(
          `/api/conversations/${conversationId}/save-as-scenario`,
          { name, anonymize },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['replay', 'scenarios'] })
    },
  })
}
